import { PaymentMethod } from '@stripe/stripe-js';

import { authServiceAxiosClient } from '@/lib';
import { withLogger } from '@/utils';

import { subscriptionsAPIUrls } from '../data';
import {
  BillingDetails,
  CreateSetupIntentResponse,
  GetBillingInfoResponse,
  GetInvoicesResponse,
  GetSubscriptionsResponse,
  UpdateBillingDetailsResponse,
  UpdatePaymentMethodResponse,
  UpdateSubscriptionResponse,
} from '../types';

const getInvoices = withLogger(async (companyID: string) => {
  const response = await authServiceAxiosClient.get<GetInvoicesResponse>(
    subscriptionsAPIUrls.getInvoices + '?cid=' + companyID,
  );

  return response.data?.data ?? [];
});

const getBillingInfo = withLogger(
  async (companyID: string, userType: 'standard' | 'admin' | 'super-admin') => {
    const response = await authServiceAxiosClient.get<GetBillingInfoResponse>(
      subscriptionsAPIUrls.getBillingInfo +
        '?cid=' +
        companyID +
        '&user_type=' +
        userType,
    );

    return response.data?.data;
  },
);

const getSubscriptions = withLogger(async (companyID: string) => {
  const response = await authServiceAxiosClient.get<GetSubscriptionsResponse>(
    subscriptionsAPIUrls.getSubscriptions + '?cid=' + companyID,
  );

  return response.data?.data ?? [];
});

const createSetupIntent = withLogger(async (companyID: string) => {
  const response = await authServiceAxiosClient.post<CreateSetupIntentResponse>(
    subscriptionsAPIUrls.createSetupIntent,
    { company_id: companyID },
  );

  return response.data?.clientSecret;
});

const updatePaymentMethod = withLogger(
  async (companyID: string, paymentMethod: PaymentMethod | string | null) => {
    await authServiceAxiosClient.post<UpdatePaymentMethodResponse>(
      subscriptionsAPIUrls.updatePaymentMethod,
      { paymentMethod, company_id: companyID },
    );
  },
);

const updateSubscription = withLogger(
  async (
    companyID: string,
    productInfo: {
      productid: number;
      tierid: number;
      licences: number;
    }[],
  ) => {
    const response =
      await authServiceAxiosClient.post<UpdateSubscriptionResponse>(
        subscriptionsAPIUrls.updateSubscription,
        {
          cid: companyID,
          productinfo: productInfo,
        },
      );

    return response.data;
  },
);

const unsubscribe = withLogger(async (companyID: string, productID: number) => {
  const response =
    await authServiceAxiosClient.post<UpdateSubscriptionResponse>(
      subscriptionsAPIUrls.updateSubscription,
      {
        cid: companyID,
        productinfo: [
          {
            productid: productID,
            tierid: 0,
            licences: 0,
          },
        ],
      },
    );

  return response.data;
});

const updateBillingDetails = withLogger(
  async (companyID: string, billingDetails: BillingDetails) => {
    await authServiceAxiosClient.post<UpdateBillingDetailsResponse>(
      subscriptionsAPIUrls.updateBillingDetails,
      { ...billingDetails, company_id: companyID },
    );
  },
);

export {
  createSetupIntent,
  getBillingInfo,
  getInvoices,
  getSubscriptions,
  unsubscribe,
  updateBillingDetails,
  updatePaymentMethod,
  updateSubscription,
};
